import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { isFakeLoginEnabled, isTestEnvironment } from '../shared/environment.util';

export interface UserInfo {
  sub: string;
  name: string;
  given_name: string;
  family_name: string;
  email: string;
  locale: string;
  updated_at: number;
  tenant_id: number;
  picture: string;
  unique_name: string;
  is_privileged: boolean;
}

export interface Entitlement {
  id: string;
  moduleId: string;
}

export interface EntitlementsResponse {
  entitlements: Entitlement[];
}

export interface MasterTenant {
  code: number;
  name: string;
  onboardingStatus: string;
  hrcoreSyncStatus: string;
  payrollSyncStatus: string;
  changeUser: string;
  changeDateTime: string;
}

/**
 * Result of a session validation call.
 * - `valid`: session cookie is valid (HTTP 200).
 * - `forbidden`: authenticated but lacking rights (HTTP 403) -> /unauthorized.
 * - `unauthenticated`: not logged in (HTTP 401) or any other failure -> login.
 */
export type ValidationResult = 'valid' | 'forbidden' | 'unauthenticated';

@Injectable({
  providedIn: 'root',
})
export class LoginInformationService {
  private readonly http = inject(HttpClient);

  private readonly baseUrl = environment.apiUrl + '/kbj-frontend/api/v1/YFAuthorization';

  private readonly masterTenantUrl = environment.apiUrl + '/kbj-frontend/api/v1/Master/MasterTenant';

  private userInfo$?: Observable<UserInfo | null>;
  private entitlements$?: Observable<EntitlementsResponse | null>;

  createFakeJwtToken(entitlement: string, tenant: string): Observable<{ token: string }> {
    // Dev-only backdoor: never invoke outside fake-login environments. The
    // backend must also return 404 for this route in production.
    if (!isFakeLoginEnabled()) {
      return throwError(() => new Error('Fake login is disabled in this environment'));
    }
    return this.http.post<{ token: string }>(
      this.baseUrl + '/fake/create-jwt-token',
      null,
      { params: { entitlement, tenant } }
    );
  }

  validate(): Observable<ValidationResult> {
    return this.http
      .get(this.baseUrl + '/me/validate', {
        withCredentials: true,
        observe: 'response',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
        params: { _: Date.now().toString() },
      })
      .pipe(
        map((response): ValidationResult => (response.status === 200 ? 'valid' : 'unauthenticated')),
        catchError((error: HttpErrorResponse) =>
          of<ValidationResult>(error.status === 403 ? 'forbidden' : 'unauthenticated')
        )
      );
  }

  getUserInfo(): Observable<UserInfo | null> {
    this.userInfo$ ??= this.http
      .get<UserInfo>(this.baseUrl + '/me/info', { withCredentials: true })
      .pipe(
        catchError(() => {
          // Never cache an invalid/unauthorized response so the next call retries.
          this.userInfo$ = undefined;
          return of(null);
        }),
        shareReplay(1)
      );
    return this.userInfo$;
  }

  getEntitlements(): Observable<EntitlementsResponse | null> {
    this.entitlements$ ??= this.http
      .get<EntitlementsResponse>(this.baseUrl + '/me/entitlements', { withCredentials: true })
      .pipe(
        catchError(() => {
          // Never cache an invalid/unauthorized response so the next call retries.
          this.entitlements$ = undefined;
          return of(null);
        }),
        shareReplay(1)
      );
    return this.entitlements$;
  }

  getMasterTenants(): Observable<MasterTenant[]> {
    // Route strips Authorization server-side; only serves login-screen tenant options.
    return this.http.get<MasterTenant[] | null>(this.masterTenantUrl).pipe(
      map((jsonData) => jsonData ?? []),
      catchError(() => of([]))
    );
  }

  getDecodedJwtToken(): Observable<unknown | null> {
    // Dev-only diagnostic endpoint: only call from non-production environments.
    if (!isTestEnvironment()) {
      return of(null);
    }
    return this.http
      .get<unknown>(this.baseUrl + '/debug/get-jwt-token', { withCredentials: true })
      .pipe(catchError(() => of(null)));
  }

  /** Drops cached user/entitlement responses so the next call re-fetches. */
  clearCache(): void {
    this.userInfo$ = undefined;
    this.entitlements$ = undefined;
  }
}
